import { useTranslation } from "@/core/i18n";
import { Button } from "atoms/button/Button";
import Close from "atoms/icon/icons/close.svg";
import { IconButton } from "atoms/icon-button/IconButton";
import { Modal } from "atoms/modal/Modal";
import { TextButton } from "atoms/text-button/TextButton";
import { isRelativeAppPath } from "modules/messages/links/classifyLink";
import { useOpenLink } from "modules/messages/links/useOpenLink";

import type { PushMessage } from "../useDeepLinkNavigation/useDeepLinkNavigation";

const linkLabel = (link: string): string => link.replace(/^(https?:\/\/)?(www\.)?/, "");

type PushMessageModalProps = {
  message: PushMessage | null;
  onClose: () => void;
};

/** The text a patient tapped a push for, with its links as buttons they choose to follow. */
export const PushMessageModal = ({ message, onClose }: PushMessageModalProps) => {
  const { t } = useTranslation();
  const { openLink } = useOpenLink();

  const followLink = async (link: string) => {
    onClose();
    await openLink(link);
  };

  return (
    <Modal
      open={message !== null}
      onClose={onClose}
      modalClassName="rounded-t-2xl"
      header={
        <div className="flex justify-end px-3.5 pt-3.5">
          <IconButton svg={Close} size="small" style="iconOnly" onClick={onClose} />
        </div>
      }
      footer={
        <div className="flex flex-col gap-3 px-6 pb-4 pt-2">
          {message?.links.map((link, index) => (
            <Button
              key={link}
              fullWidth
              style={index === 0 || isRelativeAppPath(link) ? "primary" : "secondary"}
              onClick={() => followLink(link)}
            >
              {linkLabel(link)}
            </Button>
          ))}
          <div className="flex justify-center">
            <TextButton onClick={onClose}>{t("messages.close")}</TextButton>
          </div>
        </div>
      }
    >
      <div className="flex flex-col gap-3 px-6 pb-6 pt-2">
        {message?.title && <h2 className="font-serif text-h2 text-primary">{message.title}</h2>}
        {message?.body && (
          <p className="whitespace-pre-wrap text-body1 text-secondary">{message.body}</p>
        )}
      </div>
    </Modal>
  );
};
